import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import { getGoals, createGoal, updateGoal, deleteGoal,
         createMilestone, deleteMilestone, markMilestoneReached, unmarkMilestoneReached }
  from '../../services/goals.service';
import { getActivities, addActivity, updateActivity, deleteActivity,
         updateSectorItems, getMemberFocus, updateMemberFocus, setFocusMilestone }
  from '../../services/resetPath.service';
import { getAgendaBlocks, createBlock, updateBlock, deleteBlock }
  from '../../services/idealAgenda.service';
import { getUpcomingHubEvents, updateMemberPreferences } from '../../services/hubEvents.service';
import { useAuth } from '../../context/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTORS = [
  { key: 'financial', label: 'Financial',  emoji: '💰' },
  { key: 'career',    label: 'Career',     emoji: '💼' },
  { key: 'emotional', label: 'Emotional',  emoji: '💛' },
  { key: 'mental',    label: 'Mental',     emoji: '🧠' },
  { key: 'physical',  label: 'Physical',   emoji: '💪' },
  { key: 'spiritual', label: 'Spiritual',  emoji: '🌿' },
];

const SECTOR_DOT = {
  financial: 'bg-yellow-400',
  career:    'bg-blue-400',
  emotional: 'bg-pink-400',
  mental:    'bg-purple-400',
  physical:  'bg-green-400',
  spiritual: 'bg-indigo-400',
};

const SECTOR_TAG_COLOR = {
  financial: 'bg-yellow-100 text-yellow-800',
  career:    'bg-blue-100 text-blue-800',
  emotional: 'bg-pink-100 text-pink-800',
  mental:    'bg-purple-100 text-purple-800',
  physical:  'bg-green-100 text-green-800',
  spiritual: 'bg-indigo-100 text-indigo-800',
};


// ─── Main Component ───────────────────────────────────────────────────────────

export default function Planning() {
  const [activeTab, setActiveTab] = useState('financial');

  const tabs = [
    ...SECTORS.map(s => ({ key: s.key, label: s.label, emoji: s.emoji })),
    { key: 'my-ideal-day',    label: 'My Ideal Day',    emoji: '📅' },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <PageHeading title="My Plan" subtitle="Goals, milestones, activities, and your ideal day — all in one place." />
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-6 -mx-1 px-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition
                ${activeTab === tab.key
                  ? 'bg-reset-brown text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-reset-brown/40'}`}
            >
              <span>{tab.emoji}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {SECTORS.map(s => activeTab === s.key && (
          <SectorTab key={s.key} sector={s} />
        ))}
        {activeTab === 'my-ideal-day'    && <IdealDayTab />}
      </div>
    </DashboardLayout>
  );
}

// ─── Sector Tab ───────────────────────────────────────────────────────────────

function SectorTab({ sector }) {
  const [goals, setGoals]                   = useState([]);
  const [activities, setActivities]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [newGoalText, setNewGoalText]       = useState('');
  const [addingGoal, setAddingGoal]         = useState(false);
  const [sectorFocus, setSectorFocus]       = useState('');
  const [savingFocus, setSavingFocus]       = useState(false);
  const [savedFocus, setSavedFocus]         = useState(false);
  const [focusMilestoneId, setFocusMilestoneId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allGoals, allActivities, focusData] = await Promise.all([
        getGoals(), getActivities(), getMemberFocus(),
      ]);
      setGoals(allGoals.filter(g => g.sector === sector.key));
      setActivities(allActivities.filter(a => a.sector === sector.key));
      setSectorFocus(focusData?.sector_focuses?.[sector.key] || '');
      setFocusMilestoneId(focusData?.focus_milestone?.id || null);
    } catch { /* silently handle */ }
    setLoading(false);
  }, [sector.key]);

  useEffect(() => { load(); }, [load]);

  const handleAddGoal = async () => {
    if (!newGoalText.trim()) return;
    setAddingGoal(true);
    try {
      const goal = await createGoal({ sector: sector.key, goal_text: newGoalText.trim() });
      setGoals(prev => [...prev, { ...goal, milestones: [] }]);
      setNewGoalText('');
    } catch { /* silently handle */ }
    setAddingGoal(false);
  };

  const handleArchiveGoal = async (id) => {
    await deleteGoal(id);
    setGoals(prev => prev.filter(g => g.id !== id));
  };

  const handleUpdateGoalText = async (id, text) => {
    if (!text.trim()) return;
    await updateGoal(id, { goal_text: text.trim() });
    setGoals(prev => prev.map(g => g.id === id ? { ...g, goal_text: text.trim() } : g));
  };

  const handleActivityAdded = (activity) => {
    setActivities(prev => [...prev, activity]);
  };

  const handleActivityRemoved = async (id) => {
    await deleteActivity(id);
    setActivities(prev => prev.filter(a => a.id !== id));
  };

  const handleSaveFocus = async () => {
    setSavingFocus(true);
    try {
      await updateSectorItems({ sector: sector.key, focus: sectorFocus });
      setSavedFocus(true);
      setTimeout(() => setSavedFocus(false), 3000);
    } catch { /* silently handle */ }
    setSavingFocus(false);
  };

  return loading ? (
    <div className="space-y-3 animate-pulse">
      {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-2xl" />)}
    </div>
  ) : (
    <div className="space-y-6">

      {/* Your Focus for this Sector */}
      <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
        <label className="block text-sm font-semibold text-amber-900 mb-0.5">
          Your Focus for {sector.label}
        </label>
        <p className="text-xs text-amber-700 mb-2">
          What is the most important shift you are working on here right now?
        </p>
        <div className="flex gap-2">
          <input
            className="flex-1 border border-amber-200 bg-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            value={sectorFocus}
            onChange={e => setSectorFocus(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSaveFocus()}
            placeholder={`e.g. Building consistent income, Processing grief, Sleeping before midnight…`}
          />
          <button
            onClick={handleSaveFocus}
            disabled={savingFocus}
            className="text-amber-700 text-sm font-medium px-3 hover:text-amber-900 transition-colors disabled:opacity-50"
          >
            {savingFocus ? '…' : savedFocus ? '✓' : 'Save'}
          </button>
        </div>
      </div>

      {/* Dashboard milestone callout */}
      {!focusMilestoneId ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3">
          <span className="text-lg mt-0.5">📌</span>
          <div>
            <p className="text-sm font-semibold text-amber-900">No dashboard milestone pinned yet</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Add a milestone to any goal below, then click <strong>📌</strong> next to it to pin it to your dashboard.
              You'll see it every day as your focus anchor.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-3 flex items-start gap-3">
          <span className="text-lg mt-0.5">📌</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-0.5">Pinned to dashboard</p>
            <p className="text-sm text-green-900">
              {goals.flatMap(g => g.milestones || []).find(m => m.id === focusMilestoneId)?.milestone_text
                || 'A milestone is pinned — click 📌 on any milestone below to change it.'}
            </p>
          </div>
          <button
            onClick={async () => { try { await setFocusMilestone(null); setFocusMilestoneId(null); } catch {} }}
            className="text-xs text-green-600 hover:text-green-800 shrink-0 hover:underline"
          >
            Unpin
          </button>
        </div>
      )}

      {/* Goals Section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-reset-brown text-base">My Goals for {sector.label}</h2>
          {goals.length < 4 && (
            <button
              onClick={() => document.getElementById(`new-goal-input-${sector.key}`)?.focus()}
              className="text-xs font-medium text-reset-brown border border-reset-brown/30 rounded-xl px-3 py-1.5 hover:bg-reset-brown/5 transition"
            >
              + Add goal
            </button>
          )}
        </div>

        {goals.length === 0 && (
          <p className="text-sm text-gray-400 mb-3">
            No goals yet for {sector.label}. Add up to 4 goals below.
          </p>
        )}

        <div className="space-y-3">
          {goals.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              sectorKey={sector.key}
              focusMilestoneId={focusMilestoneId}
              onArchive={handleArchiveGoal}
              onUpdate={handleUpdateGoalText}
              onMilestonesChange={(goalId, milestones) => {
                setGoals(prev => prev.map(g => g.id === goalId ? { ...g, milestones } : g));
              }}
              onSetFocusMilestone={async (milestoneId) => {
                const newId = milestoneId === focusMilestoneId ? null : milestoneId;
                try {
                  await setFocusMilestone(newId);
                  setFocusMilestoneId(newId);
                } catch { /* silently handle */ }
              }}
            />
          ))}
        </div>

        {goals.length < 4 && (
          <div className="flex gap-2 mt-3">
            <input
              id={`new-goal-input-${sector.key}`}
              type="text"
              value={newGoalText}
              onChange={e => setNewGoalText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddGoal()}
              placeholder="What do I want to achieve or maintain here?"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
            />
            <button
              onClick={handleAddGoal}
              disabled={addingGoal || !newGoalText.trim()}
              className="shrink-0 bg-reset-brown text-white rounded-xl px-4 py-2 text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
            >
              {addingGoal ? '…' : 'Add'}
            </button>
          </div>
        )}
        {goals.length >= 4 && (
          <p className="text-xs text-gray-400 mt-2">You have 4 goals for this sector — archive one to add a new one.</p>
        )}
      </section>

      {/* Activities Section */}
      <section>
        <h2 className="font-bold text-reset-brown text-base mb-3">Activities I'm Committing To</h2>
        <ActivitiesSection
          sectorKey={sector.key}
          sectorLabel={sector.label}
          activities={activities}
          onAdded={handleActivityAdded}
          onRemoved={handleActivityRemoved}
          onUpdated={(updated) => setActivities(prev => prev.map(a => a.id === updated.id ? updated : a))}
        />
      </section>
    </div>
  );
}

// ─── Goal Card ────────────────────────────────────────────────────────────────

function GoalCard({ goal, sectorKey, focusMilestoneId, onArchive, onUpdate, onMilestonesChange, onSetFocusMilestone }) {
  const [editing, setEditing]               = useState(false);
  const [editText, setEditText]             = useState(goal.goal_text);
  const [milestonesOpen, setMilestonesOpen] = useState(true);
  const [milestones, setMilestones]         = useState(goal.milestones || []);
  const [newMilestone, setNewMilestone]     = useState('');

  const saveEdit = async () => {
    await onUpdate(goal.id, editText);
    setEditing(false);
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.trim()) return;
    try {
      const m = await createMilestone(goal.id, { milestone_text: newMilestone.trim() });
      const updated = [...milestones, m];
      setMilestones(updated);
      onMilestonesChange(goal.id, updated);
      setNewMilestone('');
    } catch { /* silently handle */ }
  };

  const handleDeleteMilestone = async (id) => {
    await deleteMilestone(id);
    const updated = milestones.filter(m => m.id !== id);
    setMilestones(updated);
    onMilestonesChange(goal.id, updated);
  };

  const handleToggleMilestone = async (m) => {
    try {
      const updated = m.is_reached
        ? await unmarkMilestoneReached(m.id)
        : await markMilestoneReached(m.id);
      const newList = milestones.map(ms => ms.id === m.id ? updated : ms);
      setMilestones(newList);
      onMilestonesChange(goal.id, newList);
    } catch { /* silently handle */ }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4">
      {/* Goal row */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              type="text"
              value={editText}
              onChange={e => setEditText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(false); }}
              autoFocus
              className="w-full border border-reset-brown/40 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
            />
          ) : (
            <p className="text-sm font-medium text-gray-800">{goal.goal_text}</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <button onClick={saveEdit} className="text-xs text-reset-brown font-medium px-2 py-1 hover:underline">Save</button>
              <button onClick={() => { setEditing(false); setEditText(goal.goal_text); }} className="text-xs text-gray-400 px-2 py-1 hover:text-gray-600">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="text-xs text-gray-400 hover:text-reset-brown px-1 transition">Edit</button>
              <button onClick={() => onArchive(goal.id)} className="text-xs text-gray-400 hover:text-red-500 px-1 transition">Archive</button>
            </>
          )}
        </div>
      </div>

      {/* Milestones toggle */}
      <button
        onClick={() => setMilestonesOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-reset-brown mt-2 transition"
      >
        <span>{milestonesOpen ? '▼' : '▶'}</span>
        Milestones {milestones.length > 0 ? `(${milestones.filter(m => m.is_reached).length}/${milestones.length} reached)` : ''}
      </button>

      {milestonesOpen && (
        <div className="mt-3 pl-3 border-l-2 border-gray-100 space-y-2">
          <p className="text-xs text-gray-400">What will tell me I'm making progress?</p>

          {milestones.map(m => {
            const isFocus = m.id === focusMilestoneId;
            return (
              <div key={m.id} className="flex items-start gap-2 group">
                <button
                  onClick={() => handleToggleMilestone(m)}
                  className={`mt-0.5 text-lg leading-none ${m.is_reached ? 'text-green-500' : 'text-gray-300 hover:text-gray-400'}`}
                >
                  {m.is_reached ? '✓' : '☐'}
                </button>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm ${m.is_reached ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {m.milestone_text}
                  </span>
                  {m.is_reached && m.reached_at && (
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(m.reached_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                {/* Pin as dashboard focus — always visible */}
                <button
                  onClick={() => onSetFocusMilestone(m.id)}
                  title={isFocus ? 'Remove from dashboard focus' : 'Pin to dashboard as my focus milestone'}
                  className={`shrink-0 text-xs px-1.5 py-0.5 rounded-full border transition
                    ${isFocus
                      ? 'bg-amber-100 border-amber-300 text-amber-700'
                      : 'border-gray-200 text-gray-400 hover:border-amber-300 hover:text-amber-600'}`}
                >
                  {isFocus ? '📌 My focus' : '📌'}
                </button>
                <button
                  onClick={() => handleDeleteMilestone(m.id)}
                  className="text-gray-300 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition shrink-0"
                >
                  ✕
                </button>
              </div>
            );
          })}

          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={newMilestone}
              onChange={e => setNewMilestone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddMilestone()}
              placeholder="Add a milestone…"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
            />
            <button
              onClick={handleAddMilestone}
              className="shrink-0 text-xs bg-reset-brown text-white rounded-xl px-3 py-1.5 hover:opacity-90 transition"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Activities Section ───────────────────────────────────────────────────────

function ActivitiesSection({ sectorKey, sectorLabel, activities, onAdded, onRemoved, onUpdated }) {
  const [showForm, setShowForm]         = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saveErr, setSaveErr]           = useState('');
  const [editingId, setEditingId]       = useState(null);
  const [form, setForm]                 = useState(defaultActivityForm(sectorKey));
  const [hubEventsList, setHubEventsList]         = useState([]);
  const [loadingHubEvents, setLoadingHubEvents]   = useState(false);
  const [showDailyOptIn, setShowDailyOptIn]       = useState(false);
  const [dailyOptInFields, setDailyOptInFields]   = useState([]);
  const [dailyOptInForm, setDailyOptInForm]       = useState({ yoga_am_sms: false, yoga_am_sms_700: false, yoga_pm_email: false });
  const [dailyOptInSaving, setDailyOptInSaving]   = useState(false);
  const [dailyOptInSaved, setDailyOptInSaved]     = useState(false);
  const activityFormRef = useRef(null);
  const prevMatchKey                              = useRef('');
  const { user, updateUser } = useAuth();
  const memberTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  function defaultActivityForm(sector) {
    return {
      activity_name:        '',
      description:          '',
      frequency:            'daily',
      frequency_count:      1,
      sector_impacts:       [{ sector, direction: 'positive', magnitude: 'medium' }],
      is_non_negotiable:    false,
      untracked_sectors:    '',
      is_hub_event:         false,
      hub_event_date:       '',
      hub_event_url:        '',
      ghl_event_id:         null,
      days_of_week:         [],
      day_types:            [], // which kinds of day: green/yellow/red; [] = every kind (2026-09-29)
      time_block:           null, // optional time of day for What's on tap (2026-09-29)
      add_to_ideal_day:     false,
      ideal_day_energy:     'all',
      ideal_day_time_block: null,
    };
  }

  // Load Hub events when the toggle is first turned on
  useEffect(() => {
    if (form.is_hub_event && hubEventsList.length === 0 && !loadingHubEvents) {
      setLoadingHubEvents(true);
      getUpcomingHubEvents()
        .then(events => setHubEventsList(events || []))
        .catch(() => setHubEventsList([]))
        .finally(() => setLoadingHubEvents(false));
    }
  }, [form.is_hub_event]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Format event_datetime for display in the member's timezone */
  function formatEventTime(event) {
    if (event.event_datetime) {
      return new Date(event.event_datetime).toLocaleString('en-US', {
        timeZone:  memberTimezone,
        weekday:   'short',
        month:     'short',
        day:       'numeric',
        hour:      'numeric',
        minute:    '2-digit',
        hour12:    true,
      });
    }
    // Fallback: just show the date
    return new Date(event.event_date + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  }

  /** Called when a member selects an event from the picker */
  function handleHubEventSelect(event) {
    setForm(prev => ({
      ...prev,
      ghl_event_id:  event.ghl_event_id,
      hub_event_date: event.event_datetime || (event.event_date + 'T12:00:00'),
      hub_event_url:  event.event_url || '',
      // Pre-fill activity name if blank — useEffect below will catch it and show opt-in if it matches
      activity_name:  prev.activity_name || event.title,
    }));
  }

  // Detect activity name keywords → show delivery opt-in for matching automations
  useEffect(() => {
    const name = (form.activity_name || '').toLowerCase();
    const fields = [];
    if (name.includes('chair yoga')) fields.push('yoga_am_sms', 'yoga_am_sms_700', 'yoga_pm_email');
    if (name.includes('meditation') && !fields.includes('yoga_pm_email')) fields.push('yoga_pm_email');
    const matchKey = fields.join(',');

    // Only re-seed when the match category changes (avoids resetting on every keystroke)
    if (matchKey !== prevMatchKey.current) {
      prevMatchKey.current = matchKey;
      setDailyOptInFields(fields);
      if (fields.length > 0) {
        setDailyOptInForm({
          yoga_am_sms:     fields.includes('yoga_am_sms')     ? (user?.yoga_am_sms     || false) : false,
          yoga_am_sms_700: fields.includes('yoga_am_sms_700') ? (user?.yoga_am_sms_700 || false) : false,
          yoga_pm_email:   fields.includes('yoga_pm_email')   ? (user?.yoga_pm_email   || false) : false,
        });
        setShowDailyOptIn(true);
        setDailyOptInSaved(false);
      } else {
        setShowDailyOptIn(false);
      }
    }
  }, [form.activity_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (f, v) => setForm(prev => ({ ...prev, [f]: v }));

  const toggleImpactSector = (s) => {
    setForm(prev => {
      const exists = prev.sector_impacts.find(i => i.sector === s);
      if (exists) {
        return { ...prev, sector_impacts: prev.sector_impacts.filter(i => i.sector !== s) };
      }
      return { ...prev, sector_impacts: [...prev.sector_impacts, { sector: s, direction: 'positive', magnitude: 'medium' }] };
    });
  };

  const setImpactField = (s, field, value) => {
    setForm(prev => ({
      ...prev,
      sector_impacts: prev.sector_impacts.map(i => i.sector === s ? { ...i, [field]: value } : i),
    }));
  };

  const handleStartEdit = (activity) => {
    setEditingId(activity.id);
    setForm({
      activity_name:     activity.activity_name || '',
      description:       activity.description || '',
      frequency:         activity.frequency || 'daily',
      frequency_count:   activity.frequency_count || activity.target_count || 1,
      sector_impacts:    activity.sector_impacts || [{ sector: sectorKey, direction: 'positive', magnitude: 'medium' }],
      is_non_negotiable: activity.is_non_negotiable || false,
      untracked_sectors: activity.untracked_sectors || '',
      is_hub_event:      activity.is_hub_event || false,
      hub_event_date:    activity.hub_event_date || '',
      hub_event_url:     activity.hub_event_url || '',
      ghl_event_id:      activity.ghl_event_id || null,
      days_of_week:      activity.days_of_week || [],
      day_types:         activity.day_types || [],
      time_block:        activity.time_block || null,
    });
    setShowForm(true);
    setTimeout(() => activityFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.activity_name.trim()) return;
    setSaving(true);
    setSaveErr('');
    try {
      // hub_event_date is already an ISO string set from the event picker
      const hubDate = form.is_hub_event && form.hub_event_date
        ? new Date(form.hub_event_date).toISOString()
        : null;

      const payload = {
        sector:            sectorKey,
        activity_name:     form.activity_name.trim(),
        description:       form.description.trim() || null,
        frequency:         form.frequency,
        target_count:      form.frequency_count,
        frequency_count:   form.frequency_count,
        sector_impacts:    form.sector_impacts,
        is_non_negotiable: form.is_non_negotiable,
        untracked_sectors: form.untracked_sectors.trim() || null,
        is_hub_event:      form.is_hub_event,
        hub_event_date:    hubDate,
        hub_event_url:     form.is_hub_event && form.hub_event_url.trim() ? form.hub_event_url.trim() : null,
        ghl_event_id:      form.is_hub_event ? (form.ghl_event_id || null) : null,
        days_of_week:      form.days_of_week,
        day_types:         form.day_types || [],
        time_block:        form.time_block || null,
      };
      // The linked non-negotiable's own color restriction — separate from
      // the ideal_agenda_block created below, which only ever fed the
      // Planning page's own Ideal Day tabs, not "What's on tap" itself.
      // Create-only: the edit form doesn't load an existing activity's
      // current energy_day (see startEdit), so sending it on every save
      // would silently reset an already-color-restricted activity back to
      // 'all' the first time someone edited anything else about it.
      // Jenn, 2026-09-30: the Ideal Day toggle no longer asks its own
      // "which energy day / which time" — it uses the kinds of day and time
      // block already chosen above. Ideal Day blocks hold one colour, so a
      // single chosen colour carries over; none or several means all days.
      const idealEnergy = (form.day_types || []).length === 1 ? form.day_types[0] : 'all';
      if (!editingId) {
        payload.energy_day = idealEnergy;
      }
      if (editingId) {
        const updated = await updateActivity(editingId, payload);
        if (onUpdated) onUpdated(updated);
        setEditingId(null);
      } else {
        const activity = await addActivity(payload);
        onAdded(activity);
        // Optionally add to Ideal Day in one step
        if (form.add_to_ideal_day) {
          try {
            await createBlock({
              block_title:  form.activity_name.trim(),
              energy_day:   idealEnergy,
              time_block:   form.time_block || null,
              days_of_week: form.days_of_week || [],
            });
          } catch { /* non-critical — activity already saved */ }
        }
      }
      setForm(defaultActivityForm(sectorKey));
      setShowForm(false);
    } catch (err) {
      setSaveErr(err?.response?.data?.error || 'Could not save activity. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div>
      {activities.length === 0 && !showForm && (
        <p className="text-sm text-gray-400 mb-3">No activities yet for {sectorLabel}.</p>
      )}

      {activities.length > 0 && (
        <div className="space-y-2 mb-3">
          {activities.map(a => (
            <div key={a.id} className="bg-white rounded-2xl shadow-sm px-4 py-3 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-800">{a.activity_name}</span>
                  <span className="text-xs text-gray-400">
                    {a.frequency_count || a.target_count}× {a.frequency}
                  </span>
                  {a.is_non_negotiable && (
                    <span className="text-xs bg-reset-brown/10 text-reset-brown rounded-full px-2 py-0.5">Non-negotiable</span>
                  )}
                </div>
                {a.sector_impacts && a.sector_impacts.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {a.sector_impacts.map(imp => (
                      <span key={imp.sector} className={`flex items-center gap-1 text-xs rounded-full px-2 py-0.5 ${SECTOR_TAG_COLOR[imp.sector] || 'bg-gray-100 text-gray-700'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full inline-block ${SECTOR_DOT[imp.sector] || 'bg-gray-400'}`} />
                        {imp.sector} · {imp.direction} · {imp.magnitude}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0 ml-3">
                <button
                  onClick={() => handleStartEdit(a)}
                  className="text-xs text-gray-400 hover:text-reset-brown transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => onRemoved(a.id)}
                  className="text-xs text-gray-400 hover:text-red-500 transition"
                >
                  Archive
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => {
            setShowForm(true);
            setTimeout(() => activityFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
          }}
          className="text-sm font-medium text-reset-brown border border-reset-brown/30 rounded-xl px-4 py-2 hover:bg-reset-brown/5 transition"
        >
          + Add Activity
        </button>
      )}

      {showForm && (
        <form ref={activityFormRef} onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-4 space-y-4 mt-2">
          <h4 className="text-sm font-semibold text-reset-brown">
            {editingId ? 'Edit activity' : 'Add an activity'}
          </h4>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">What is the activity?</label>
            <input
              type="text"
              value={form.activity_name}
              onChange={e => setField('activity_name', e.target.value)}
              placeholder="e.g. Walk for 30 minutes"
              required
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
            />
          </div>

          {/* Daily delivery opt-in — appears when activity name matches a known automation */}
          {showDailyOptIn && !dailyOptInSaved && dailyOptInFields.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-reset-brown">Get this delivered to you</p>
                  <p className="text-xs text-gray-500 mt-0.5">Want us to send it automatically?</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDailyOptIn(false)}
                  className="text-gray-300 hover:text-gray-500 text-xs mt-0.5 shrink-0"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-2">
                {[
                  { field: 'yoga_am_sms',     label: '📱 SMS — Morning Chair Yoga at 9:30am ET' },
                  { field: 'yoga_am_sms_700', label: '📱 SMS — Morning Chair Yoga at 7:00am ET' },
                  { field: 'yoga_pm_email',   label: '📧 Email — Evening Yoga & Meditation at 8pm ET' },
                ].filter(opt => dailyOptInFields.includes(opt.field)).map(({ field, label }) => (
                  <label key={field} className="flex items-center gap-3 cursor-pointer">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={dailyOptInForm[field]}
                      onClick={() => setDailyOptInForm(f => ({ ...f, [field]: !f[field] }))}
                      className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none shrink-0 ${
                        dailyOptInForm[field] ? 'bg-reset-terracotta' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        dailyOptInForm[field] ? 'translate-x-4' : 'translate-x-0'
                      }`} />
                    </button>
                    <span className="text-xs text-reset-brown">{label}</span>
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowDailyOptIn(false)}
                  className="text-xs text-gray-400 hover:text-gray-600 transition"
                >
                  Skip
                </button>
                <button
                  type="button"
                  disabled={dailyOptInSaving}
                  onClick={async () => {
                    setDailyOptInSaving(true);
                    try {
                      const payload = {};
                      dailyOptInFields.forEach(f => { payload[f] = dailyOptInForm[f]; });
                      await updateMemberPreferences(payload);
                      updateUser(payload);
                      setDailyOptInSaved(true);
                    } catch { /* silently fail */ }
                    setDailyOptInSaving(false);
                  }}
                  className="ml-auto px-4 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition disabled:opacity-50"
                >
                  {dailyOptInSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {showDailyOptIn && dailyOptInSaved && (
            <p className="text-xs text-green-700 font-medium px-1">✓ Delivery preferences saved.</p>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Description (optional)</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              placeholder="Any details about how you do this…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">How often?</label>
              <select
                value={form.frequency}
                onChange={e => setField('frequency', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="w-28">
              <label className="block text-xs font-semibold text-gray-700 mb-1">How many times?</label>
              <input
                type="number"
                min="1"
                max="99"
                value={form.frequency_count}
                onChange={e => setField('frequency_count', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Which sectors does this impact?</label>
            <div className="space-y-3">
              {SECTORS.map(s => {
                const impact = form.sector_impacts.find(i => i.sector === s.key);
                return (
                  <div key={s.key}>
                    <label className="flex items-center gap-2 cursor-pointer mb-1">
                      <input
                        type="checkbox"
                        checked={!!impact}
                        onChange={() => toggleImpactSector(s.key)}
                        className="rounded"
                      />
                      <span className={`w-2 h-2 rounded-full ${SECTOR_DOT[s.key]}`} />
                      <span className="text-sm text-gray-700">{s.label}</span>
                    </label>

                    {impact && (
                      <div className="ml-6 flex gap-4 mt-1">
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Impact direction</span>
                          <div className="flex gap-2">
                            {['positive', 'negative'].map(d => (
                              <button
                                key={d}
                                type="button"
                                onClick={() => setImpactField(s.key, 'direction', d)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium border transition
                                  ${impact.direction === d
                                    ? 'bg-reset-brown text-white border-reset-brown'
                                    : 'border-gray-200 text-gray-600 hover:border-reset-brown/40'}`}
                              >
                                {d.charAt(0).toUpperCase() + d.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Magnitude</span>
                          <div className="flex gap-2">
                            {['low', 'medium', 'high'].map(m => (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setImpactField(s.key, 'magnitude', m)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium border transition
                                  ${impact.magnitude === m
                                    ? 'bg-reset-brown text-white border-reset-brown'
                                    : 'border-gray-200 text-gray-600 hover:border-reset-brown/40'}`}
                              >
                                {m.charAt(0).toUpperCase() + m.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Jenn, 2026-09-29: the "Is this a non-negotiable?" switch is gone — every
              activity shows in "What's on tap for today" on its days and kinds of day. */}
          {/* Days of week — which days should this appear on the dashboard? */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Which days? <span className="font-normal text-gray-400">(leave blank = every day)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const days = form.days_of_week;
                    setField('days_of_week', days.includes(day)
                      ? days.filter(d => d !== day)
                      : [...days, day]);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium capitalize border transition
                    ${form.days_of_week.includes(day)
                      ? 'bg-reset-brown text-white border-reset-brown'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-reset-brown/40'}`}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Which kinds of day — Jenn, 2026-09-29: "they set the activity for when they
              want to do it (day, frequency, and type of day) ... On a red day for me,
              that shouldn't show at all." Blank = every kind of day. */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Which kinds of day? <span className="font-normal text-gray-400">(leave blank = every kind of day)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'green',  label: 'Green day',  emoji: '🟢' },
                { key: 'yellow', label: 'Yellow day', emoji: '🟡' },
                { key: 'red',    label: 'Red day',    emoji: '🔴' },
              ].map(opt => {
                const on = (form.day_types || []).includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setField('day_types', on ? form.day_types.filter(d => d !== opt.key) : [...(form.day_types || []), opt.key])}
                    className={`flex items-center gap-1.5 min-h-[40px] px-3 py-1.5 rounded-xl text-sm font-medium border transition
                      ${on ? 'bg-reset-brown text-white border-reset-brown' : 'bg-white border-gray-200 text-gray-600 hover:border-reset-brown/40'}`}
                  >
                    <span className="text-base leading-none">{opt.emoji}</span> {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">It shows in "What's on tap" only on the kinds of day you pick here.</p>
          </div>

          {/* Optional time of day — What's on tap lists things in this order (2026-09-29). */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Which time of day? <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {[{ key: null, label: '📋 Any time' }, ...TIME_BLOCKS].map(tb => {
                const on = (form.time_block || null) === tb.key;
                return (
                  <button
                    key={tb.key || 'any'}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setField('time_block', tb.key)}
                    className={`min-h-[40px] px-3 py-1.5 rounded-xl text-sm font-medium border transition
                      ${on ? 'bg-reset-brown text-white border-reset-brown' : 'bg-white border-gray-200 text-gray-600 hover:border-reset-brown/40'}`}
                  >
                    {tb.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Other area this impacts outside the six sectors (optional)
            </label>
            <input
              type="text"
              value={form.untracked_sectors}
              onChange={e => setField('untracked_sectors', e.target.value)}
              placeholder="e.g. Parenting, Home, Community, Relationships…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={form.is_hub_event}
              onClick={() => setField('is_hub_event', !form.is_hub_event)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition
                ${form.is_hub_event ? 'bg-reset-brown' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
                ${form.is_hub_event ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-gray-700">Is this a Hub live event?</span>
          </div>

          {form.is_hub_event && (
            <div className="space-y-2 pl-4 border-l-2 border-amber-200">
              <p className="text-xs font-semibold text-gray-700">Choose a Hub event:</p>

              {loadingHubEvents && (
                <p className="text-xs text-gray-400 italic">Loading upcoming Hub events…</p>
              )}

              {!loadingHubEvents && hubEventsList.length === 0 && (
                <p className="text-xs text-gray-400 italic">
                  No upcoming Hub events found. Try again after the nightly sync.
                </p>
              )}

              {!loadingHubEvents && hubEventsList.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-gray-200 p-2 bg-gray-50">
                  {hubEventsList.map(ev => {
                    const isSelected = form.ghl_event_id === ev.ghl_event_id;
                    return (
                      <button
                        key={ev.ghl_event_id}
                        type="button"
                        onClick={() => handleHubEventSelect(ev)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition
                          ${isSelected
                            ? 'bg-reset-brown text-white'
                            : 'hover:bg-amber-50 text-gray-700'}`}
                      >
                        <span className="font-semibold block">{ev.title}</span>
                        <span className={isSelected ? 'text-amber-100' : 'text-gray-400'}>
                          {formatEventTime(ev)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Show selected event confirmation */}
              {form.ghl_event_id && (
                <p className="text-xs text-amber-700 font-medium">
                  ✓ Event selected — join link will show on your dashboard on the day of the event.
                </p>
              )}

            </div>
          )}

          {/* Also add to My Ideal Day */}
          {!editingId && (
            <div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.add_to_ideal_day}
                  onClick={() => setField('add_to_ideal_day', !form.add_to_ideal_day)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition
                    ${form.add_to_ideal_day ? 'bg-reset-brown' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
                    ${form.add_to_ideal_day ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-gray-700">Also add to My Ideal Day</span>
              </div>

              {form.add_to_ideal_day && (
                <p className="mt-1.5 text-xs text-gray-500">
                  It goes into My Ideal Day using the kinds of day and time of day you picked above.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setShowForm(false); setEditingId(null); setForm(defaultActivityForm(sectorKey)); }}
              className="flex-1 text-sm text-gray-500 border border-gray-200 rounded-xl py-2 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.activity_name.trim()}
              className="flex-1 bg-reset-brown text-white rounded-xl py-2 text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingId ? 'Update Activity' : 'Save Activity'}
            </button>
          </div>
          {saveErr && (
            <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2 mt-1">{saveErr}</p>
          )}
        </form>
      )}
    </div>
  );
}

// (The Non-Negotiables tab was removed 2026-09-29 — every activity now shows in What's on tap.)

// ─── Ideal Day Tab ────────────────────────────────────────────────────────────

// ─── Ideal Day — constants ─────────────────────────────────────────────────────

const TIME_BLOCKS = [
  { key: 'early_morning', label: '🌅 Early Morning', hint: 'Before 8am'  },
  { key: 'morning',       label: '☀️ Morning',        hint: '8am – 12pm' },
  { key: 'mid_day',       label: '🌤 Mid-Day',        hint: '12pm – 3pm' },
  { key: 'afternoon',     label: '🌇 Afternoon',       hint: '3pm – 6pm'  },
  { key: 'early_evening', label: '🌆 Early Evening',  hint: '6pm – 8pm'  },
  { key: 'late_evening',  label: '🌙 Late Evening',   hint: 'After 8pm'  },
];

const ENERGY_TABS = [
  { key: 'green',  emoji: '🟢', label: 'Green Day',  sub: '3.5+ spoons — productive & full',   active: 'bg-green-100 border-green-400 text-green-800',   dot: 'bg-green-400'  },
  { key: 'yellow', emoji: '🟡', label: 'Yellow Day', sub: '2–3.5 spoons — gentle & paced',     active: 'bg-yellow-100 border-yellow-400 text-yellow-800', dot: 'bg-yellow-400' },
  { key: 'red',    emoji: '🔴', label: 'Red Day',    sub: '0–1.9 spoons — rest & minimum',     active: 'bg-red-100 border-red-400 text-red-800',         dot: 'bg-red-400'    },
];

function defaultItemForm(timeBlock = null, energyDay = 'all') {
  return { block_title: '', start_time: '', days_of_week: [], time_block: timeBlock, energy_day: energyDay };
}

function IdealDayTab() {
  const [blocks, setBlocks]       = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('green');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [addingTo, setAddingTo]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(defaultItemForm());
  const addFormRef = useRef(null);

  useEffect(() => {
    Promise.all([getAgendaBlocks(), getActivities()])
      .then(([blks, acts]) => { setBlocks(blks); setActivities(acts); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const blocksForTB   = (tab, tbKey) => blocks.filter(b => (b.energy_day === tab || b.energy_day === 'all') && b.time_block === tbKey);
  const unscheduled   = (tab)        => blocks.filter(b => (b.energy_day === tab || b.energy_day === 'all') && !b.time_block);

  const startAdding = (key) => {
    setAddingTo(key);
    setForm(defaultItemForm(key === 'unscheduled' ? null : key, activeTab));
    setEditingId(null);
    setEditForm({});
    setTimeout(() => addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  const handleSave = async () => {
    if (!form.block_title.trim()) return;
    setSaving(true);
    try {
      const block = await createBlock({
        block_title:  form.block_title.trim(),
        days_of_week: form.days_of_week,
        start_time:   form.start_time || null,
        time_block:   form.time_block,
        energy_day:   form.energy_day,
      });
      setBlocks(prev => [...prev, block]);
      setAddingTo(null);
      setForm(defaultItemForm());
    } catch { /* silently handle */ }
    setSaving(false);
  };

  const handleUpdate = async (id) => {
    if (!editForm.block_title?.trim()) return;
    setSaving(true);
    try {
      const updated = await updateBlock(id, {
        block_title:  editForm.block_title.trim(),
        start_time:   editForm.start_time || null,
        days_of_week: editForm.days_of_week || [],
      });
      setBlocks(prev => prev.map(b => b.id === id ? updated : b));
      setEditingId(null);
      setEditForm({});
    } catch { /* silently handle */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlock(id);
      setBlocks(prev => prev.filter(b => b.id !== id));
    } catch { /* silently handle */ }
  };

  const dot = ENERGY_TABS.find(t => t.key === activeTab)?.dot || 'bg-gray-300';

  // Inline render helpers (not components — avoids reconciliation/focus issues)
  const dayBtns = (days, onToggle) => (
    <div className="flex flex-wrap gap-1 items-center">
      <span className="text-[10px] text-gray-400 mr-0.5">Days:</span>
      {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => (
        <button key={day} type="button" onClick={() => onToggle(day)}
          className={`px-2 py-0.5 rounded-lg text-[10px] font-medium capitalize border transition
            ${days.includes(day)
              ? 'bg-reset-brown text-white border-reset-brown'
              : 'bg-white text-gray-500 border-gray-200 hover:border-reset-brown/40'}`}
        >{day.slice(0,3)}</button>
      ))}
    </div>
  );

  const renderBlock = (block) => editingId === block.id ? (
    <div key={block.id} className="px-4 py-3 space-y-2 bg-amber-50/60 border-b border-amber-100">
      <input type="text" autoFocus value={editForm.block_title}
        onChange={e => setEditForm(f => ({ ...f, block_title: e.target.value }))}
        onKeyDown={e => {
          if (e.key === 'Enter')  handleUpdate(block.id);
          if (e.key === 'Escape') { setEditingId(null); setEditForm({}); }
        }}
        className="w-full border border-reset-brown/40 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
      />
      {block.time_block && (
        <input type="time" value={editForm.start_time}
          onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))}
          className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none"
        />
      )}
      {dayBtns(editForm.days_of_week || [], d => setEditForm(f => ({
        ...f, days_of_week: (f.days_of_week || []).includes(d)
          ? (f.days_of_week || []).filter(x => x !== d)
          : [...(f.days_of_week || []), d],
      })))}
      <div className="flex gap-2 pt-0.5">
        <button type="button" onClick={() => handleUpdate(block.id)} disabled={saving}
          className="text-xs bg-reset-brown text-white rounded-lg px-3 py-1.5 font-medium hover:opacity-90 disabled:opacity-50">
          {saving ? '…' : 'Save'}
        </button>
        <button type="button" onClick={() => { setEditingId(null); setEditForm({}); }}
          className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
      </div>
    </div>
  ) : (
    <div key={block.id} className="px-4 py-2.5 flex items-center gap-3 group border-b border-gray-50">
      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-gray-800">{block.block_title}</span>
        {block.start_time && <span className="text-xs text-gray-400 ml-2">{formatTime(block.start_time)}</span>}
        {block.energy_day === 'all' && (
          <span className="ml-2 text-[10px] text-gray-400 bg-gray-100 rounded-full px-1.5 py-0.5">all days</span>
        )}
        {block.days_of_week?.length > 0 && (
          <span className="text-[10px] text-gray-400 ml-1">
            · {block.days_of_week.map(d => d.slice(0,3)).join(', ')}
          </span>
        )}
      </div>
      <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition">
        <button onClick={() => {
            setEditingId(block.id);
            setEditForm({ block_title: block.block_title, start_time: block.start_time || '', days_of_week: block.days_of_week || [] });
            setAddingTo(null);
          }}
          className="text-xs text-gray-400 hover:text-reset-brown transition" title="Edit">✏️</button>
        <button onClick={() => handleDelete(block.id)}
          className="text-xs text-gray-400 hover:text-red-500 transition" title="Remove">🗑️</button>
      </div>
    </div>
  );

  const renderAddForm = (placeholder, showTime) => (
    <div ref={addFormRef} className="px-4 py-3 space-y-2 bg-gray-50 border-t border-gray-100">
      <input type="text" autoFocus value={form.block_title}
        onChange={e => setForm(f => ({ ...f, block_title: e.target.value }))}
        onKeyDown={e => {
          if (e.key === 'Enter')  handleSave();
          if (e.key === 'Escape') { setAddingTo(null); setForm(defaultItemForm()); }
        }}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-brown/30"
      />
      {showTime && (
        <input type="time" value={form.start_time}
          onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
          className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none"
        />
      )}
      {dayBtns(form.days_of_week, d => setForm(f => ({
        ...f, days_of_week: f.days_of_week.includes(d)
          ? f.days_of_week.filter(x => x !== d)
          : [...f.days_of_week, d],
      })))}
      <div className="flex gap-2 pt-0.5">
        <button type="button" onClick={handleSave} disabled={saving || !form.block_title.trim()}
          className="text-xs bg-reset-brown text-white rounded-lg px-3 py-1.5 font-medium hover:opacity-90 disabled:opacity-50">
          {saving ? '…' : 'Add'}
        </button>
        <button type="button" onClick={() => { setAddingTo(null); setForm(defaultItemForm()); }}
          className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
      </div>
    </div>
  );

  const SectionFooter = ({ label, sectionKey, isAdding }) => (
    <div className="px-4 py-2 border-t border-gray-50">
      <button
        onClick={() => !isAdding && startAdding(sectionKey)}
        disabled={isAdding}
        className={`text-xs font-medium transition ${isAdding ? 'text-gray-300 cursor-default' : 'text-reset-brown hover:opacity-80'}`}
      >
        {isAdding ? 'Adding…' : label}
      </button>
    </div>
  );

  return loading ? (
    <div className="space-y-2 animate-pulse">
      {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-200 rounded-2xl" />)}
    </div>
  ) : (
    <div className="space-y-5">

      <p className="text-sm text-gray-500">
        Plan three versions of your ideal day based on your energy. The right version shows automatically on your dashboard based on how you check in each day.
      </p>

      {/* Energy day tabs */}
      <div className="flex gap-2">
        {ENERGY_TABS.map(tab => (
          <button key={tab.key}
            onClick={() => { setActiveTab(tab.key); setAddingTo(null); setEditingId(null); setEditForm({}); }}
            className={`flex-1 flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-2xl border-2 transition
              ${activeTab === tab.key ? tab.active : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
          >
            <span className="text-lg leading-none">{tab.emoji}</span>
            <span className="text-xs font-semibold mt-0.5">{tab.label}</span>
            <span className="text-[10px] opacity-70 text-center leading-tight hidden sm:block">{tab.sub}</span>
          </button>
        ))}
      </div>

      {/* Time block sections */}
      <div className="space-y-3">
        {TIME_BLOCKS.map(tb => {
          const items    = blocksForTB(activeTab, tb.key);
          const isAdding = addingTo === tb.key;
          return (
            <div key={tb.key} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-gray-800 text-sm">{tb.label}</span>
                  <span className="text-xs text-gray-400">{tb.hint}</span>
                </div>
                {items.length > 0 && <span className="text-xs text-gray-400">{items.length}</span>}
              </div>

              {items.length === 0 && !isAdding && (
                <p className="px-4 py-2.5 text-xs text-gray-400 italic">Nothing here yet.</p>
              )}

              {items.map(block => renderBlock(block))}

              {isAdding && renderAddForm(`Add to ${tb.label}…`, true)}

              <SectionFooter label={`+ Add to ${tb.label}`} sectionKey={tb.key} isAdding={isAdding} />
            </div>
          );
        })}

        {/* Unscheduled */}
        {(() => {
          const items    = unscheduled(activeTab);
          const isAdding = addingTo === 'unscheduled';
          return (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-gray-800 text-sm">📋 Fit These In</span>
                  <span className="text-xs text-gray-400">No specific time</span>
                </div>
                {items.length > 0 && <span className="text-xs text-gray-400">{items.length}</span>}
              </div>

              {items.length === 0 && !isAdding && (
                <p className="px-4 py-2.5 text-xs text-gray-400 italic">Nothing here yet.</p>
              )}

              {items.map(block => renderBlock(block))}

              {isAdding && renderAddForm('Add something to fit in…', false)}

              <SectionFooter label="+ Add unscheduled item" sectionKey="unscheduled" isAdding={isAdding} />
            </div>
          );
        })()}
      </div>

      {/* Committed activities — read-only reference */}
      {activities.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
            <span className="font-semibold text-gray-800 text-sm">🎯 My Committed Activities</span>
            <span className="text-xs text-gray-400 ml-2">from your sector plans</span>
          </div>
          <div>
            {activities.map(a => (
              <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 border-b border-gray-50 last:border-0">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${SECTOR_DOT[a.sector] || 'bg-gray-300'}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-800">{a.activity_name}</span>
                  {a.is_non_negotiable && <span className="ml-1.5 text-xs">🔒</span>}
                  <span className="text-xs text-gray-400 ml-2">{a.frequency_count || 1}× {a.frequency}</span>
                </div>
                {a.sector && (
                  <span className={`text-xs rounded-full px-2 py-0.5 shrink-0 ${SECTOR_TAG_COLOR[a.sector] || 'bg-gray-100 text-gray-700'}`}>
                    {a.sector}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(timeStr) {
  if (!timeStr) return '';
  try {
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'pm' : 'am';
    const hour  = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
  } catch {
    return timeStr;
  }
}
